import React, { useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Button,
  Modal,
  Carousel,
} from "react-bootstrap";
import {
  FaCouch,
  FaCar,
  FaWeightHanging,
  FaExclamationTriangle,
  FaRulerCombined,
  FaBox,
  FaCalendar,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaTimes,
  FaExpand,
  FaTrashAlt,
  FaUser,
  FaTruckLoading,
  FaFileInvoice,
  FaQuestionCircle,
  FaRoute,
  FaClock,
  FaTruck,
  FaClipboardList,
  FaStar,
} from "react-icons/fa";
import { TbBackground, TbListDetails } from "react-icons/tb";
import { AiFillIdcard } from "react-icons/ai";
import { HiWrenchScrewdriver } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import { RouteMap } from "../../RouteMap";
import { formatNumber } from "../../../utils/helpers";
import DriverDetailsModal from "./DriverDetailsModal";
import { formatDisplayDate } from "../../../utils/helpers";
import { TransportImage } from "../transportImages";
import { driverAPI } from "../../../api/apiSystem";
import DriverRatingModal from "../../rating/RatingModal";
import Swal from "sweetalert2";
import DriverRatingDisplay from "../../rating/DriverRatingDisplay";
import { RiPinDistanceFill } from "react-icons/ri";
import { sendRatingChatMessage } from "../../../utils/ratingChat";

// مكون لعرض العناصر
const ItemsSection = ({
  itemTypes,
  numItems,
  dimensions,
  itemWeights,
  weight,
  board_number,
  making_year,
  driving,
  isCar = false,
  showBoardNumber = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-3">
      <h6 className="fw-bold text-secondary mb-3">
        {t("driverCard.transportDetails.items")} ({itemTypes?.length})
      </h6>
      {itemTypes?.map((type, index) => (
        <div key={index} className="mb-3 p-3 border rounded bg-light">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <Badge bg="primary">#{index + 1}</Badge>
            <span className="fw-bold">
              {numItems?.[index] || 1} × {t(type) || type}
            </span>
          </div>
          <div className="row small dark-info-label flex-wrap">
            {dimensions?.[index] && (
              <div className="col-6 mb-1">
                <FaRulerCombined className="me-1" />{" "}
                {dimensions?.[index] || "-"} cm
              </div>
            )}
            {itemWeights?.[index] && (
              <div className="col-6 mb-1">
                <FaWeightHanging className="me-1" />
                {t("driverCard.transportDetails.weight")}:{" "}
                {itemWeights?.[index] || 0} kg
              </div>
            )}
            {weight?.[index] && (
              <div className="col-6 mb-1">
                <FaWeightHanging className="me-1" />
                {t("driverCard.transportDetails.weight")}:{" "}
                {weight?.[index] || 0} kg
              </div>
            )}
            {isCar && (
              <>
                {showBoardNumber && (
                  <div className="col-6 mb-1">
                    <AiFillIdcard className="me-1" />
                    {t("driverCard.transportDetails.boardNumber")}:{" "}
                    {board_number?.[index] || "-"}
                  </div>
                )}
                <div className="col-6 mb-1">
                  <FaCalendar className="me-1" />
                  {t("driverCard.transportDetails.makingYear")}:{" "}
                  {making_year?.[index] || "-"}
                </div>
                <div className="col-6 mb-1">
                  <HiWrenchScrewdriver className="me-1" />
                  {t("driverCard.transportDetails.driving")}:{" "}
                  {driving?.[index] === "1" ? t("yes") : t("no")}
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// مكون للمعاملة الخاصة
const SpecialTreatmentSection = ({
  special_treatment,
  special_treatment_details,
}) => {
  const { t } = useTranslation();

  if (special_treatment !== "1") return null;

  return (
    <div className="mt-3 mb-3 p-3 border rounded bg-light">
      <h6 className="fw-bold">
        <FaExclamationTriangle className="me-1" />
        {t("driverCard.transportDetails.specialTreatment")}
      </h6>
      <p className="mb-0">
        {special_treatment_details || t("driverCard.general.notAvailable")}
      </p>
    </div>
  );
};

// مكون للتفاصيل الإضافية
const AdditionalDetailsSection = ({
  item,
  isCar = false,
  showTransportDetails,
}) => {
  const { t } = useTranslation();

  const PAYMENT_TERM_OPTIONS = [
    { value: "on_delivery", label: t("delivery_summary.paymentTerms.onDelivery", "Bei Lieferung") },
    { value: "7_days", label: t("delivery_summary.paymentTerms.after7Days", "7 Tage nach Lieferung") },
    { value: "14_days", label: t("delivery_summary.paymentTerms.after14Days", "14 Tage nach Lieferung") },
    { value: "30_days", label: t("delivery_summary.paymentTerms.after30Days", "30 Tage nach Lieferung") },
    { value: "3_days", label: t("delivery_summary.paymentTerms.after3Days", "3 Tage nach Lieferung") },
    { value: "by_agreement", label: t("delivery_summary.paymentTerms.byAgreement", "Nach Vereinbarung") },
  ];

  return (
    <div className="mt-3">
      <h6 className="fw-bold text-secondary mb-3">
        {t("driverCard.transportDetails.additionalDetails")}
      </h6>

      <Row className="g-2">
        {/* معلومات التحميل والتفريغ */}
        <Col md={6}>
          <Card className="h-100 border-0 bg-light">
            <Card.Body>
              <h6 className="fw-bold text-primary mb-3">
                {t("driverCard.transportDetails.loadingInfo")}
              </h6>

              {/* loading_elevator - يظهر فقط إذا كانت قيمته "1" أو "0" */}
              {(item.loading_elevator !== null || item.loading_elevator) && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.transportDetails.loadingElevator")}:
                  </strong>{" "}
                  {item.loading_elevator
                    ? t("delivery_summary.yes")
                    : t("delivery_summary.no")}
                </div>
              )}

              {/* loading_floor - يظهر فقط إذا كانت قيمته موجودة */}
              {item.loading_floor && item.loading_floor !== "" && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.transportDetails.loadingFloor")}:
                  </strong>{" "}
                  {t("driverCard.transportDetails.floor")} {item.loading_floor}
                </div>
              )}

              {item.loading_date && item.loading_date !== "" && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.transportDetails.loadingDate")}:
                  </strong>{" "}
                  {formatDisplayDate(item.loading_date) || item.loading_date}
                </div>
              )}

              {item.loading_time && item.loading_time !== "" && (
                <div>
                  <strong>
                    {t("driverCard.transportDetails.loadingTime")}:
                  </strong>{" "}
                  {item.loading_time}
                </div>
              )}

              {showTransportDetails && (
                <>
                  {item.loading_name && item.loading_name !== "" && (
                    <div>
                      <strong>
                        {t("clientTransportDetails.loading")}{" "}
                        {t("clientDetails.pickup.name")}:
                      </strong>{" "}
                      {item.loading_name}
                    </div>
                  )}

                  {item.loading_streat && item.loading_streat !== "" && (
                    <div>
                      <strong>
                        {t("clientTransportDetails.loading")}{" "}
                        {t("clientDetails.pickup.address")}:
                      </strong>{" "}
                      {item.loading_streat}
                    </div>
                  )}

                  {item.loading_phone && item.loading_phone !== "" && (
                    <div>
                      <strong>
                        {t("clientTransportDetails.loading")}{" "}
                        {t("clientDetails.pickup.phone")}:
                      </strong>{" "}
                      {item.loading_phone}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100 border-0 bg-light">
            <Card.Body>
              <h6 className="fw-bold text-primary mb-3">
                {t("driverCard.transportDetails.unloadingInfo")}
              </h6>

              {/* unloading_elevator - يظهر فقط إذا كانت قيمته "1" أو "0" */}
              {(item.unloading_elevator !== null ||
                item.unloading_elevator) && (
                  <div className="mb-2">
                    <strong>
                      {t("driverCard.transportDetails.unloadingElevator")}:
                    </strong>{" "}
                    {item.unloading_elevator
                      ? t("delivery_summary.yes")
                      : t("delivery_summary.no")}
                  </div>
                )}

              {/* unloading_floor - يظهر فقط إذا كانت قيمته موجودة */}
              {item.unloading_floor && item.unloading_floor !== "" && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.transportDetails.unloadingFloor")}:
                  </strong>{" "}
                  {t("driverCard.transportDetails.floor")}{" "}
                  {item.unloading_floor}
                </div>
              )}

              {item.unloading_date && item.unloading_date !== "" && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.transportDetails.unloadingDate")}:
                  </strong>{" "}
                  {formatDisplayDate(item.unloading_date) || item.unloading_date}
                </div>
              )}

              {item.unloading_time && item.unloading_time !== "" && (
                <div>
                  <strong>

                    {t("driverCard.transportDetails.unloadingTime")}:

                  </strong>{" "}
                  {item.unloading_time}
                </div>
              )}

              {showTransportDetails && (
                <>
                  {item.unloading_name && item.unloading_name !== "" && (
                    <div>
                      <strong>
                        {t("clientTransportDetails.unloading")}{" "}
                        {t("clientDetails.pickup.name")}:
                      </strong>{" "}
                      {item.unloading_name}
                    </div>
                  )}

                  {item.unloading_streat && item.unloading_streat !== "" && (
                    <div>
                      <strong>
                        {t("clientTransportDetails.unloading")}{" "}
                        {t("clientDetails.pickup.address")}:
                      </strong>{" "}
                      {item.unloading_streat}
                    </div>
                  )}

                  {item.unloading_phone && item.unloading_phone !== "" && (
                    <div>
                      <strong>
                        {t("clientTransportDetails.unloading")}{" "}
                        {t("clientDetails.pickup.phone")}:
                      </strong>{" "}
                      {item.unloading_phone}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {item.payment_term && (
          <div className="detail-row">
            <div>{t("delivery_summary.paymentTerm.title", "Zahlungsziel")}</div>
            <div className="small-muted">
              {PAYMENT_TERM_OPTIONS.find(
                (option) => option.value === item.payment_term
              )?.label || "—"}
            </div>
          </div>
        )}
      </Row>

      {item.load_helping && (
        <div className="mt-3 p-3 border rounded bg-light">
          <strong>{t("driverCard.transportDetails.loadHelp")}:</strong>{" "}
          {item.load_helping === "0"
            ? t("delivery_summary.no_help")
            : item.load_helping === "1"
              ? t("delivery_summary.partial_help")
              : item.load_helping === "2"
                ? t("delivery_summary.full_help")
                : item.load_helping}
        </div>
      )}

      <DriverRatingDisplay
        rating={item.driverRating}
        comment={item.driverRating?.comment || item.comment}
      />

      {/* معلومات الأثاث */}
      {/* {!isCar && (
        <Row className="mt-3">
          <Col md={12}>
            <Card className="border-0 bg-info bg-opacity-10">
              <Card.Body>
                <h6 className="fw-bold text-info mb-3">
                  <FaCouch className="me-2" />
                  {t("driverCard.transportDetails.furnitureSpecificInfo")}
                </h6>

                <div className="row">
                  <div className="col-6">
                    <strong>
                      {t("driverCard.transportDetails.deliveryAs")}:
                    </strong>{" "}
                    {t(item.delivery_as) || "-"}
                  </div>
                  <div className="col-6">
                    {(item.item_breakable === "1" ||
                      item.item_breakable === "0") && (
                      <>
                        <strong>
                          {t("driverCard.transportDetails.itemBreakable")}:
                        </strong>{" "}
                        {item.item_breakable === "1" ? t("yes") : t("no")}
                      </>
                    )}
                  </div>
                </div>

                {item.item_breakable_details && (
                  <div className="mt-2">
                    <strong>
                      {t("driverCard.transportDetails.breakableDetails")}:
                    </strong>{" "}
                    {item.item_breakable_details}
                  </div>8
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )} */}
    </div>
  );
};

const ImageGallery = ({ images, className = "" }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { t } = useTranslation();

  if (!images || images.length === 0) {
    return null;
  }

  const handleImageClick = (index) => {
    setSelectedIndex(index);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      {/* معرض الصور المصغر */}
      <div className={`image-gallery ${className}`}>
        <h6 className="fw-bold text-secondary mb-2">
          {t("driverCard.transportDetails.images")} ({images.length})
        </h6>
        <div className="d-flex flex-wrap gap-2">
          {images.slice(0, 4).map((image, index) => (
            <div
              key={index}
              className="thumbnail-container position-relative"
              style={{ cursor: "pointer" }}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={image}
                alt={`Transport ${index + 1}`}
                className="thumbnail img-thumbnail"
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
              <div className="thumbnail-overlay">
                <FaExpand className="text-white" size={12} />
              </div>

              {/* مؤشر الصور الإضافية */}
              {index === 3 && images.length > 4 && (
                <div className="more-images-badge">+{images.length - 4}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Modal لتكبير الصور */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        className="image-modal"
      >
        <Modal.Header className="border-0 pb-0">
          <div className="d-flex justify-content-between align-items-center w-100">
            <h6 className="mb-0 text-dark">
              {t("driverCard.transportDetails.images")} ({selectedIndex + 1} /{" "}
              {images.length})
            </h6>
            <Button
              variant="link"
              className="text-dark p-0"
              onClick={handleCloseModal}
            >
              <FaTimes size={20} />
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body className="text-center p-0">
          <Carousel
            activeIndex={selectedIndex}
            onSelect={setSelectedIndex}
            interval={null}
            indicators={images.length > 1}
            controls={images.length > 1}
          >
            {images.map((image, index) => (
              <Carousel.Item key={index}>
                <div className="d-flex justify-content-center align-items-center">
                  <img
                    src={image}
                    alt={`Transport ${index + 1}`}
                    className="img-fluid modal-image"
                    style={{
                      maxHeight: "70vh",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </Modal.Body>
      </Modal>
    </>
  );
};

// المكون الرئيسي
const TransportDetailsPage = ({
  type,
  item,
  order,
  onChat,
  unreadCount,
  shouldShowChat,
  itemReceived,
  onReceive,
  onDeliver,
  onDelete,
  onClose,
  driver,
  setSelectedId,
  setSelectedItemForMap,
  setModalData,
  setShowModal,
  deleteLoading,
  receivedLoading,
  deliverLoading,
  showReceive = false,
  showDeliver = false,
  showDelete = false,
  showDriverDetails = false,
  showRatingCustomer = false,
  showTransportDetails = false,
  showChat = false,
  showBoardNumber = false,
}) => {
  const { t } = useTranslation();
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showRateCustomer, setShowRateCustomer] = useState(false);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  const isCar = type === "cars";
  const isFurniture = type === "furnitures";
  const itemsCount = item.item_type?.length || 0;

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  const transportId =
    order?.transport?.id ||
    order?.transport_id ||
    item?.id ||
    item?.transport_id ||
    null;

  const orderId = order?.id || null;

  // بيانات العميل للعرض في المودال
  const customerPerson = {
    name:
      order?.customer_name ||
      order?.user?.firstname ||
      [order?.user?.firstname, order?.user?.lastname].filter(Boolean).join(" ") ||
      order?.transport?.user?.firstname ||
      t("driverCard.general.customer", "Kunde"),
    id:
      order?.user_id ||
      order?.transport?.user_id ||
      order?.customer_id ||
      "—",
    avatar: order?.user?.avatar || order?.customer_avatar || null,
  };

  const handleDriverRateCustomer = async (payload) => {
    if (!transportId || !orderId) {
      Swal.fire({
        icon: "error",
        title: t("driverCard.alerts.error"),
        text: t(
          "rating.missing_ids",
          "Transport oder Auftrag nicht gefunden",
        ),
      });
      return;
    }

    try {
      setRatingSubmitting(true);

      const res = await driverAPI.transports.rateCustomer(
        transportId,
        orderId,
        payload, // { punctuality, communication, cargo_care, rating, comment }
      );

      if (res?.success) {
        await sendRatingChatMessage({
          apiSendMessage: driverAPI.transportOrderChat.sendMessage,
          transportId,
          orderId,
          item,           // الـ transport details
          order,          // الـ order
          scores: payload,
          direction: "driver_to_customer",
        });
        
        Swal.fire({
          icon: "success",
          title: t("rating.success_title", "Danke!"),
          text: t(
            "rating.success_customer",
            "Ihre Bewertung wurde gespeichert",
          ),
          timer: 2500,
          showConfirmButton: false,
        });
        setShowRateCustomer(false);
      } else {
        Swal.fire({
          icon: "error",
          title: t("driverCard.alerts.error"),
          text:
            res?.message ||
            t("rating.error", "Bewertung konnte nicht gespeichert werden"),
        });
      }
    } catch (err) {
      console.error("❌ Rate customer error:", err);
      Swal.fire({
        icon: "error",
        title: t("driverCard.alerts.error"),
        text:
          err?.response?.data?.message ||
          err?.message ||
          t("rating.error", "Bewertung konnte nicht gespeichert werden"),
      });
    } finally {
      setRatingSubmitting(false);
    }
  };

  const parseCoordinates = (coordString) => {
    if (!coordString) return null;
    try {
      const [lat, lng] = coordString
        .split(",")
        .map((coord) => parseFloat(coord.trim()));
      return [lat, lng];
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  };

  const fromCoords = useMemo(
    () => parseCoordinates(item.booking_from_lat_long),
    [item.booking_from_lat_long],
  );
  const toCoords = useMemo(
    () => parseCoordinates(item.booking_to_lat_long),
    [item.booking_to_lat_long],
  );

  if (!fromCoords || !toCoords) {
    return (
      <div className="text-danger text-center p-3">
        {t("driverCard.general.invalidCoords")}
      </div>
    );
  }

  return (
    <>
      <div className="dark-three-columns">
        {/* Section 1: Route */}
        <div className="dark-section">
          <div className="dark-section-title">
            <FaRoute />
            <span>{t("driverCard.general.route")}</span>
          </div>
          <div className="dark-section-body">
            {/* Map */}
            <div className="dark-map-wrapper">
              <RouteMap fromCoords={fromCoords} toCoords={toCoords} />
            </div>

            {/* pickup & delivery */}
            <div className="dark-route-stats py-3 justify-content-between flex-wrap">
  
              <div className="dark-route-stat gap-2 flex-row" style={{borderRight: "1px solid rgb(212 212 212)"}}>
                <FaMapMarkerAlt className="stat-icon text-danger" style={{ fontSize: "25px" }} />
                <div className="p-2 rounded">
                  <div className="text-center">
                    <span className="stat-value d-block">{item.booking_from}</span>
                    <span className="stat-label">
                      {t("driverCard.general.pickup")}
                    </span>
                  </div>
                </div>
              </div>
  
              <div className="dark-route-stat gap-2 flex-row">
                <FaMapMarkerAlt className="stat-icon text-success" style={{ fontSize: "25px" }} />
                <div className="p-2 rounded">
                  <div className="text-center">
                    <span className="stat-value d-block">{item.booking_to}</span>
                    <span className="stat-label">
                      {t("driverCard.general.delivery")}
                    </span>
                  </div>
                </div>
              </div>
  
            </div>
  
            {/* Route Stats */}
            <div className="dark-route-stats justify-content-between">
  
              <div className="dark-route-stat gap-2 flex-row">
                <RiPinDistanceFill className="stat-icon" style={{ fontSize: "25px" }} />
                <div className="text-center">
                  <span className="stat-value d-block">{(item.distance || "0 km").replace("km", " km")}</span>
                  <span className="stat-label">
                    {t("driverCard.general.distance")}
                  </span>
                </div>
              </div>
  
              <div className="dark-route-stat gap-2 flex-row">
                <FaClock className="stat-icon" style={{ fontSize: "25px" }} />
                <div className="text-center">
                  <span className="stat-value d-block">{item.total_duration || "-"}</span>
                  <span className="stat-label">
                    {t("driverCard.general.duration")}
                  </span>
                </div>
              </div>
  
            </div>
          </div>
        </div>

        {/* Section 2: Vehicle & Actions */}
        <div className="dark-section">
          <div className="dark-section-title">
            <h6 className="mb-0">
              {isCar ? (
                <FaCar className="me-2" />
              ) : (
                <FaCouch className="me-2" />
              )}
              {t("driverCard.transportDetails.transportDetails")}
            </h6>
          </div>
          <div className="dark-section-body">
            {/* Vehicle Info */}
              <div className="mb-3">
                <h6 className="fw-bold text-secondary">
                  {t("driverCard.transportDetails.transportType")}
                </h6>
                <Badge bg={isCar ? "primary" : "info"} className="fs-6">
                  {t(item.transport_type) ||
                    t("driverCard.general.notAvailable")}
                </Badge>
              </div>

              {/* العناصر */}
              <ItemsSection
                itemTypes={item.item_type}
                numItems={item.num_items}
                dimensions={item.dimensions}
                itemWeights={item.item_weight}
                weight={item.weight}
                board_number={item.board_number}
                making_year={item.making_year}
                driving={item.driving}
                isCar={isCar}
                showBoardNumber={showBoardNumber}
              />

              {/* الوزن الإجمالي */}
              <div className="border-top pt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold text-dark">{t('driverCard.transportDetails.totalWeight')}:</span>
                  <span className="badge bg-success fs-6">{formatNumber(item.total_weight)} kg</span>
                </div>
              </div>

              {item.malfunction && (
                <div className="info-item mb-2">
                  <FaQuestionCircle className="icon" />
                  <span className="label">
                    {t("dynamic_form.help_carry_question")}:
                  </span>
                  <span className="value">
                    {item.malfunction === 1
                      ? t("dynamic_form.yes")
                      : t("dynamic_form.no")}
                  </span>
                  ,<span className="value">{item.malfunction_details}</span>
                </div>
              )}

              {/* أزرار الإجراءات */}
              <div className="mt-auto">
                {showDriverDetails && order && (
                  <div className="my-3">
                    <Button
                      className="w-100 py-2 fw-bold"
                      variant="info"
                      onClick={() => setShowDriverModal(true)}
                    >
                      <FaUser className="me-2" />
                      {t("driverCard.buttons.viewDriverDetails")}
                    </Button>
                  </div>
                )}

                {showRatingCustomer && !order.customerRating && (
                  <div className="my-3">
                    <Button
                      className="w-100 py-2 fw-bold border-0"
                      style={{ background: "#0d9f6e" }}
                      onClick={() => setShowRateCustomer(true)}
                    >
                      <FaStar className="me-2" />
                      {t("rating.rate_customer", "Kunde bewerten")}
                    </Button>
                  </div>
                )}

                {/* أزرار receive/deliver */}
                {showReceive && !itemReceived && (
                  <Button
                    variant="orange"
                    className="bg-orange w-100 py-2 fw-bold justify-content-center"
                    onClick={() => onReceive(order)}
                    disabled={receivedLoading}
                  >
                    <FaTruckLoading className="me-2" />
                    {receivedLoading
                      ? t("driverRequest.buttons.loading")
                      : t("driverRequest.buttons.receive")}
                  </Button>
                )}

                {showDeliver && itemReceived && (
                  <Button
                    className="bg-orange w-100 py-2 fw-bold"
                    onClick={() => onDeliver(order)}
                    disabled={deliverLoading}
                  >
                    <FaTruckLoading className="me-2" />
                    {deliverLoading
                      ? t("driverRequest.buttons.loading")
                      : t("driverRequest.buttons.deliver")}
                  </Button>
                )}

                {/* أزرار الشات والحذف */}
                <div className="d-flex gap-2 mb-2">
                  {showChat && onChat && (
                    <Button
                      variant="success"
                      className="flex-fill mt-2 position-relative"
                      onClick={() => onChat(order)}
                    >
                      💬 {t("driverOffer.buttons.chat")}
                      {unreadCount > 0 && (
                        <Badge
                          bg="danger"
                          className="position-absolute top-0 start-100 translate-middle"
                          style={{ fontSize: "0.6rem" }}
                        >
                          {unreadCount}
                        </Badge>
                      )}
                    </Button>
                  )}

                  {showDelete && onDelete && (
                    <Button
                      variant="danger"
                      className="flex-fill mt-2"
                      onClick={() => onDelete(order)}
                      disabled={deleteLoading}
                    >
                      <FaTrashAlt className="me-2" />
                      {deleteLoading
                        ? t("driverOffer.buttons.deleting")
                        : t("driverOffer.buttons.delete")}
                    </Button>
                  )}
                </div>

                {/* زر الإغلاق */}
                {onClose && (
                  <Button
                    className="w-100 py-2 fw-bold d-md-block d-none"
                    variant="secondary"
                    onClick={onClose}
                  >
                    {t("driverCard.buttons.close")}
                  </Button>
                )}

                {/* السعر المقترح */}
                {userType === "driver" && (
                  <>
                    {(order?.price || item?.price) && (
                      <div className="text-center mt-2">
                        <small className="dark-info-label">
                          {t("driverCard.priceDetails.priceSuggestion")}:
                        </small>
                        <div className="fw-bold text-success fs-5">
                          {order?.price || item?.price} EUR (Netto)
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
          </div>
        </div>

        {/* Section 3: Shipment Details */}
        <div className="dark-section">
          <div className="dark-section-title">
            <TbListDetails className="me-2" />
            {t("driverCard.transportDetails.actionsAndImages")}
          </div>
          <div className="dark-section-body">
            {/* Shipment Info - Show total items count */}
            <div className="mb-4">
              <h6 className="fw-bold text-secondary mb-3">
                {t("driverCard.orderDetails.details")}
              </h6>
              <div className="row text-center">
                <div className="col-6">
                  <div className="border-end">
                    <div className="fw-bold text-dark">{itemsCount}</div>
                    <small className="dark-info-label">
                      {t("driverCard.general.items")}
                    </small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="fw-bold text-dark">
                    {t(item.delivery_as) || "-"}
                  </div>
                  <small className="dark-info-label">
                    {t("driverCard.transportDetails.deliveryAs")}
                  </small>
                </div>
              </div>
            </div>

              {item.images && item.images.length > 0 && (
                <div className="mb-3">
                  <ImageGallery images={item.images} />
                </div>
              )}

              {/* التفاصيل الإضافية */}
              <AdditionalDetailsSection
                item={item}
                isCar={isCar}
                showTransportDetails={showTransportDetails}
              />
          </div>
        </div>
      </div>

      <DriverRatingModal
        show={showRateCustomer}
        onHide={() => setShowRateCustomer(false)}
        role="customer"
        person={customerPerson}
        onSubmit={handleDriverRateCustomer}
        submitting={ratingSubmitting}
      />

      <DriverDetailsModal
        show={showDriverModal}
        onHide={() => setShowDriverModal(false)}
        driverData={order}
        type="transport"
      />
    </>
  );
};

export default TransportDetailsPage;
